import { useCallback, useRef, useState } from "react";
import { useT } from "../i18n";
import { useDismiss } from "../components/useDismiss";
import { label, SPEEDS } from "./speed";

/**
 * SpeedButton is the playback rate, in the transport beside Play.
 *
 * A button showing the current rate that opens the five above it, rather than
 * one that cycles. Cycling is fine going up and four taps going back, and the
 * only way to find out what it will do next is to press it.
 *
 * It opens upwards because the transport is pinned to the bottom of the screen,
 * and it closes on a tap anywhere else — a menu in a fixed bar that only closes
 * on its own button is a menu covering the end of the chapter.
 */
export default function SpeedButton({
  speed,
  onChange,
}: {
  speed: number;
  onChange: (speed: number) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  useDismiss(open, box, close);

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        data-testid="speed"
        aria-label={t.player.speedOf(label(speed))}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`grid h-11 min-w-11 place-items-center rounded-full border px-2 text-sm tabular transition-colors ${
          speed === 1
            ? "border-line text-ink-soft hover:bg-sunk hover:text-ink"
            : "border-sage bg-sage/15 font-medium text-ink"
        }`}
      >
        {label(speed)}
      </button>
      {open && (
        <ul
          data-testid="speed-menu"
          role="listbox"
          aria-label={t.player.speed}
          /* start-0, not end-0: the menu is wider than its button and grows
             away from whichever edge it is pinned to. The speed button is the
             first control in the transport, so pinning the far edge sent the
             menu off the side of the screen. */
          className="absolute bottom-full start-0 mb-2 min-w-20 overflow-hidden rounded-card border border-line bg-surface py-1 shadow-lg"
        >
          {SPEEDS.map((s) => (
            <li key={s}>
              <button
                type="button"
                role="option"
                aria-selected={s === speed}
                data-testid={`speed-${s}`}
                onClick={() => {
                  onChange(s);
                  setOpen(false);
                }}
                className={`block w-full px-3 py-2 text-center text-sm tabular transition-colors hover:bg-sunk ${
                  s === speed ? "bg-sage/15 font-medium text-ink" : "text-ink-soft"
                }`}
              >
                {label(s)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
